import { CriteriaData } from "../../../../types";

export interface QuestionResponse {
  id: number;
  date: string;
  questions: {
    question: string;
    response: string | number;
  }[];
  responder: string; // Add responder
  summary?: Record<string, string>;
}

export interface MultiScaleDataPointWithId {
  id: number;
  date: string;
  subscales: { name: string; score: number; interpretation: string }[];
  responder: string; // Add responder
}

interface AssessmentSummaryProps {
  selectedData: QuestionResponse[];
  longitudinalData?: MultiScaleDataPointWithId[];
  criteriaData?: CriteriaData[];
}

export function AssessmentSummary({
  selectedData,
  longitudinalData,
  criteriaData,
}: AssessmentSummaryProps) {
  if (selectedData.length === 0) return null;

  // Check if any of the selected data has a summary or scores
  const hasContent = selectedData.some((d) => {
    const hasSummary = d.summary && Object.keys(d.summary).length > 0;
    const longEntry = longitudinalData?.find((l) => l.id === d.id);
    const hasScores = (longEntry?.subscales?.length || 0) > 0;
    return hasSummary || hasScores;
  });

  if (!hasContent) {
    return (
      <div className="p-4 text-center italic text-slate-500 text-sm">
        No additional summary data available for the selected assessment(s).
      </div>
    );
  }

  return (
    <div
      className="grid gap-4"
      style={{
        gridTemplateColumns: `repeat(${selectedData.length}, minmax(0, 1fr))`,
      }}
    >
      {selectedData.map((data) => {
        const summary = data.summary || {};
        const longEntry = longitudinalData?.find((l) => l.id === data.id);
        const subscales = longEntry?.subscales || [];
        const criteriaEntry = criteriaData?.find((c) => c.id === data.id);
        const combinedADHD = criteriaEntry?.assessments.find(
          (a) => a.name === "ADHD Combined",
        );

        return (
          <div
            key={data.id}
            className="flex flex-col gap-3 p-4 bg-slate-50 rounded-lg border shadow-sm"
            style={{ borderColor: "#E2E8F0" }}
          >
            <div
              className="font-bold text-sm border-b pb-2 mb-1 uppercase tracking-wider text-slate-700"
              style={{ borderColor: "#CBD5E1" }}
            >
              {data.date} {data.responder ? `- ${data.responder}` : ""}
            </div>

            {/* Quantitative Scores */}
            {subscales.length > 0 && (
              <div
                className="flex flex-col gap-2 mb-2 pb-3 border-b"
                style={{ borderColor: "#E2E8F0" }}
              >
                <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
                  Calculated Scores
                </div>
                <div className="grid gap-2">
                  {subscales.map((s) => (
                    <div
                      key={s.name}
                      className="flex justify-between items-center bg-white px-2 py-1.5 rounded border border-slate-100 shadow-sm"
                    >
                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-tight">
                        {s.name}
                      </span>
                      <span className="text-sm font-bold text-slate-800">
                        {s.score}
                        {s.interpretation && s.interpretation !== "N/A" && (
                          <span
                            className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                              s.interpretation === "Criteria Met" ||
                              s.interpretation === "Positive" ||
                              s.interpretation === "Elevated" ||
                              s.interpretation === "action-needed"
                                ? "bg-red-50 text-red-600 border border-red-100"
                                : "bg-slate-100 text-slate-600 border border-slate-200"
                            }`}
                          >
                            {s.interpretation}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {combinedADHD && (
                    <div className="flex justify-between items-center bg-white px-2 py-1.5 rounded border border-slate-100 shadow-sm">
                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-tight">
                        {combinedADHD.name}
                      </span>
                      <span className="text-sm font-bold text-slate-800">
                        <span
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                            combinedADHD.result === "Criteria Met"
                              ? "bg-red-50 text-red-600 border border-red-100"
                              : "bg-slate-50 text-slate-500 border border-slate-100"
                          }`}
                        >
                          {combinedADHD.result}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Qualitative Summary Metadata */}
            <div className="grid gap-3">
              {Object.entries(summary).map(([key, value]) => (
                <div key={key} className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-tight">
                    {key}
                  </span>
                  <span className="text-sm text-slate-700 font-medium leading-relaxed whitespace-pre-wrap">
                    {value}
                  </span>
                </div>
              ))}
            </div>
            {Object.keys(summary).length === 0 && subscales.length === 0 && (
              <div className="text-xs italic text-slate-400">
                No summary metadata available
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
